import { useState, type ReactNode } from "react";
import {
  MapPin,
  Link as LinkIcon,
  Calendar,
  Clock,
  Euro,
  Building2,
  LogIn,
  LogOut,
  Tag,
  BadgeCheck,
  ArrowRight,
  FileText,
} from "lucide-react";
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { useI18n } from "@/hooks/useI18n";
import { UnsplashAttribution } from "@/components/ui/UnsplashAttribution";
import { priceTagKey } from "../models/price-tag";
import { MediaCarouselModal } from "@/components/ui/MediaCarouselModal";
import { MediaItem } from "@/types/media.types";
import { ActivityRoomsGuests } from "../activities-card/ActivityFields";
import type { ActivityType, RateHawkBookingMetadata } from "../models/activity.model";

/**
 * Shape "piatta" e completamente opzionale di un'attività, sufficiente per la
 * sola visualizzazione. Ogni modale del builder passa il proprio `formData`
 * (Partial<XxxActivity>), che è assegnabile a questa interfaccia.
 */
export interface ActivityDetailData {
  type?: ActivityType;
  title?: string;
  description?: string;
  link?: string;
  address?: string;
  location?: string;
  start_date?: string;
  end_date?: string;
  start_time?: string;
  end_time?: string;
  check_in_time?: string;
  check_out_time?: string;
  check_in_note?: string;
  cost?: number;
  include_price?: boolean;
  refundable_rate?: boolean;
  price_tag?: string;
  transport_type?: "train" | "bus" | "car" | "taxi" | "metro" | "other";
  company?: string;
  metadata?: RateHawkBookingMetadata;
  media?: MediaItem[];
  documentation?: { name: string; url: string }[];
  travels?: Array<{
    flightNumber?: string;
    airline?: string;
    from?: string;
    to?: string;
    link?: string;
    fromDate?: string;
    fromTime?: string;
    toDate?: string;
    toTime?: string;
    address?: string;
    date?: string;
    time?: string;
  }>;
}

interface ActivityDetailViewProps {
  isOpen: boolean;
  onClose: () => void;
  icon?: ReactNode;
  title: ReactNode;
  activity: ActivityDetailData;
  /**
   * Nasconde i campi riservati al TA (costo/importo) quando la card è mostrata
   * al viaggiatore (es. proposta condivisa / timeline in modalità traveler).
   * I dati sensibili dovrebbero comunque non arrivare dal backend nello shared,
   * ma questo flag è una difesa aggiuntiva lato UI.
   */
  hidePrivateFields?: boolean;
}

// ── Primitive di presentazione (private al modulo) ──────────────────────────

/**
 * Coppia etichetta/valore con icona, pensata per una griglia compatta.
 * `note` è una precisazione fra parentesi accanto all'etichetta (es. per
 * segnalare al TA che il campo non viene mostrato al viaggiatore).
 */
const Field = ({
  icon,
  label,
  note,
  children,
}: {
  icon: ReactNode;
  label: string;
  note?: string;
  children: ReactNode;
}) => (
  <div className="flex items-start gap-2.5">
    <span className="mt-0.5 shrink-0 text-gray-400">{icon}</span>
    <div className="min-w-0">
      <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500">
        {label}
        {note && (
          <span className="ml-1 normal-case tracking-normal italic text-gray-400">
            ({note})
          </span>
        )}
      </dt>
      <dd className="text-sm text-gray-900 break-words">{children}</dd>
    </div>
  </div>
);

/** Blocco con titolo per le sezioni più ricche (voli, percorso, note…). */
const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="space-y-2.5">
    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    {children}
  </section>
);

// ── Helper di formattazione ─────────────────────────────────────────────────

const ICON = "h-4 w-4";

const formatDate = (value?: string): string => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateRange = (start?: string, end?: string): string => {
  const s = formatDate(start);
  const e = formatDate(end);
  if (s && e && s !== e) return `${s} — ${e}`;
  return s || e || "";
};

const joinRange = (a?: string, b?: string): string => {
  if (a && b) return `${a} — ${b}`;
  return a || b || "";
};

const formatPrice = (cost?: number): string => {
  if (cost === undefined || cost === null || Number.isNaN(cost)) return "";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "EUR",
  }).format(cost);
};

/**
 * Vista di sola lettura di un'attività, condivisa da tutte le modali del
 * builder quando aperte dalla Libreria (`readOnly`). Un solo componente
 * data-driven: mostra i campi comuni in una griglia compatta e i blocchi
 * specifici per tipo (segmenti volo, percorso trasporto) solo quando presenti.
 */
const ActivityDetailView = ({
  isOpen,
  onClose,
  icon,
  title,
  activity,
  hidePrivateFields = false,
}: ActivityDetailViewProps) => {
  const { t } = useI18n();

  // Indice della foto aperta nel carosello; null = carosello chiuso.
  const [carouselIndex, setCarouselIndex] = useState<number | null>(null);

  const media = activity.media ?? [];
  const documents = activity.documentation?.filter((d) => d?.url) ?? [];
  const travels = activity.travels ?? [];
  const isFlight = activity.type === "flight";
  const isTransport = activity.type === "transportation";

  const address = activity.address || activity.location || "";
  const dateRange = formatDateRange(activity.start_date, activity.end_date);
  const timeRange = joinRange(activity.start_time, activity.end_time);
  const checkInOut = joinRange(activity.check_in_time, activity.check_out_time);
  // Il costo è un dato riservato al TA: nascosto quando la card è per il viaggiatore.
  const price = hidePrivateFields ? "" : formatPrice(activity.cost);
  // Il link esterno è materiale di lavoro del TA (fornitore, tariffa, OTA) e
  // resta nascosto al viaggiatore, tranne per l'alloggio: lì è la pagina della
  // struttura, utile anche al cliente.
  const isLinkPublic = activity.type === "accommodation";
  const showLink = !!activity.link && (isLinkPublic || !hidePrivateFields);
  const roomsGuests = activity.metadata?.ratehawk_guests_per_room;
  const hasRoomsGuests = !!(roomsGuests && roomsGuests.length > 0);

  /**
   * Con il carosello aperto sopra il dettaglio, Esc viene intercettato da
   * entrambe le modali: qui chiudiamo solo il carosello, così il dettaglio resta
   * aperto. La chiusura del carosello rimette anche `overflow: hidden` sul body,
   * che la modale figlia resetta smontandosi mentre il dettaglio è ancora aperto.
   */
  const handleClose = () => {
    if (carouselIndex !== null) {
      closeCarousel();
      return;
    }
    onClose();
  };

  const closeCarousel = () => {
    setCarouselIndex(null);
    document.body.style.overflow = "hidden";
  };

  // Le sezioni "ricche" per tipo sono i segmenti volo / le tappe trasporto.
  const showTravels = (isFlight || isTransport) && travels.length > 0;

  const hasFacts =
    !!address ||
    showLink ||
    !!activity.company ||
    (isTransport && !!activity.transport_type) ||
    !!dateRange ||
    !!timeRange ||
    !!checkInOut ||
    !!price ||
    !!activity.price_tag ||
    activity.refundable_rate === true;

  const hasContent =
    hasFacts ||
    hasRoomsGuests ||
    showTravels ||
    !!activity.check_in_note ||
    !!activity.description ||
    media.length > 0 ||
    documents.length > 0;

  const transportRoleLabel = (index: number): string => {
    if (index === 0) return t("common.from");
    if (index === travels.length - 1) return t("common.to");
    return t("modals.stopLabel");
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <div className="flex items-center gap-2">
          {icon} {title}
        </div>
      }
      size="lg"
      footer={
        <div className="flex w-full justify-end">
          <Button type="button" size="sm" variant="ghost" onClick={onClose}>
            {t("common.close")}
          </Button>
        </div>
      }
    >
      <div className="space-y-6 p-6 pt-4">
        {/* Galleria immagini */}
        {media.length > 0 && (
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            {media.map((item, i) => (
              <div key={`${item.url}-${i}`} className="relative overflow-hidden rounded-lg border border-gray-200">
                <button
                  type="button"
                  onClick={() => setCarouselIndex(i)}
                  className="block w-full cursor-pointer"
                  aria-label={item.alt || `Media ${i + 1}`}
                >
                  <img
                    src={item.url}
                    alt={item.alt || ""}
                    className="h-24 w-full object-cover transition-transform duration-200 hover:scale-105"
                  />
                </button>
                <UnsplashAttribution item={item} />
              </div>
            ))}
          </div>
        )}

        {/* Camere & ospiti (alloggio prenotato via RateHawk) */}
        {hasRoomsGuests && (
          <div className="text-sm text-gray-700">
            <ActivityRoomsGuests metadata={activity.metadata} />
          </div>
        )}

        {/* Griglia informazioni principali */}
        {hasFacts && (
          <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
            {address && (
              <Field icon={<MapPin className={ICON} />} label={t("modals.address")}>
                {address}
              </Field>
            )}

            {showLink && (
              <Field
                icon={<LinkIcon className={ICON} />}
                label={t("modals.link")}
                note={isLinkPublic ? undefined : t("modals.detail.hiddenFromClient")}
              >
                <a
                  href={activity.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline break-all"
                >
                  {activity.link}
                </a>
              </Field>
            )}

            {isTransport && activity.transport_type && (
              <Field icon={<Building2 className={ICON} />} label={t("modals.detail.transportType")}>
                {t(`modals.transportTypes.${activity.transport_type}`)}
              </Field>
            )}

            {activity.company && (
              <Field icon={<Building2 className={ICON} />} label={t("modals.company")}>
                {activity.company}
              </Field>
            )}

            {dateRange && (
              <Field icon={<Calendar className={ICON} />} label={t("modals.date")}>
                {dateRange}
              </Field>
            )}

            {timeRange && (
              <Field icon={<Clock className={ICON} />} label={t("modals.time")}>
                {timeRange}
              </Field>
            )}

            {activity.check_in_time && (
              <Field icon={<LogIn className={ICON} />} label={t("modals.checkIn")}>
                {activity.check_in_time}
              </Field>
            )}

            {activity.check_out_time && (
              <Field icon={<LogOut className={ICON} />} label={t("modals.checkOut")}>
                {activity.check_out_time}
              </Field>
            )}

            {price && (
              <Field icon={<Euro className={ICON} />} label={t("modals.price")}>
                {price}
                {activity.include_price === false && (
                  <span className="ml-2 text-xs italic text-gray-400">
                    {t("modals.detail.notIncludedInQuote")}
                  </span>
                )}
              </Field>
            )}

            {activity.price_tag && (
              <Field icon={<Tag className={ICON} />} label={t("modals.detail.priceTag")}>
                {priceTagKey(activity.price_tag) ? t(priceTagKey(activity.price_tag)!) : activity.price_tag}
              </Field>
            )}

            {activity.refundable_rate === true && (
              <Field icon={<BadgeCheck className={ICON} />} label={t("modals.refundableRate")}>
                {t("common.yes")}
              </Field>
            )}
          </dl>
        )}

        {/* Segmenti volo */}
        {isFlight && showTravels && (
          <Section title={t("modals.travelRoute")}>
            <div className="space-y-2">
              {travels.map((seg, i) => (
                <div key={i} className="space-y-2 rounded-lg border border-gray-200 p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-900">
                      {seg.airline || t("modals.flightN", { n: i + 1 })}
                    </span>
                    {seg.flightNumber && (
                      <span className="text-xs text-gray-500">{seg.flightNumber}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                    <span>{seg.from}</span>
                    <ArrowRight className="h-4 w-4 text-gray-400" />
                    <span>{seg.to}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                    <span>{[formatDate(seg.fromDate), seg.fromTime].filter(Boolean).join(" ")}</span>
                    <ArrowRight className="h-3.5 w-3.5" />
                    <span>{[formatDate(seg.toDate), seg.toTime].filter(Boolean).join(" ")}</span>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Percorso trasporto */}
        {isTransport && showTravels && (
          <Section title={t("modals.travelRoute")}>
            <ol className="space-y-3">
              {travels.map((stop, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-16 shrink-0 pt-0.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">
                    {transportRoleLabel(i)}
                  </span>
                  <div className="min-w-0 text-sm">
                    <div className="text-gray-900 break-words">{stop.address}</div>
                    {(stop.date || stop.time) && (
                      <div className="text-xs text-gray-500">
                        {[formatDate(stop.date), stop.time].filter(Boolean).join(" ")}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </Section>
        )}

        {/* Note check-in */}
        {activity.check_in_note && (
          <Section title={t("modals.detail.checkInNote")}>
            <p className="whitespace-pre-line text-sm text-gray-700">
              {activity.check_in_note}
            </p>
          </Section>
        )}

        {/* Descrizione (HTML formattato dal RichTextEditor) */}
        {activity.description && (
          <Section title={t("modals.description")}>
            <div className="text-sm text-gray-700">
              <RichTextEditor value={activity.description} onChange={() => {}} readOnly />
            </div>
          </Section>
        )}

        {/* Documenti */}
        {documents.length > 0 && (
          <Section title={t("modals.documentation")}>
            <ul className="space-y-2">
              {documents.map((doc, i) => (
                <li key={i}>
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-lg border border-gray-200 p-2.5 text-sm text-gray-900 transition-colors hover:bg-gray-50"
                  >
                    <FileText className="h-4 w-4 shrink-0 text-gray-400" />
                    <span className="truncate">{doc.name}</span>
                  </a>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Fallback: nessun dettaglio */}
        {!hasContent && (
          <p className="py-8 text-center text-sm italic text-gray-400">
            {t("modals.detail.noDetails")}
          </p>
        )}
      </div>
    </Modal>

    {/* Carosello interno: sostituisce l'apertura della foto in una nuova scheda.
        Modale annidata: il componente Modal calcola lo z-index in base alle modali
        già montate, quindi si sovrappone correttamente al dettaglio attività. */}
    <MediaCarouselModal
      isOpen={carouselIndex !== null}
      onClose={closeCarousel}
      media={media}
      initialIndex={carouselIndex ?? 0}
    />
    </>
  );
};

export default ActivityDetailView;
